var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef } from 'react';
import { css } from '@emotion/react';
import isPropValid from '@emotion/is-prop-valid';
import styled from '../../styles/styled';
import { typography } from '../../styles/style-mixins';
const baseStyles = ({ theme }) => css `
  font-weight: ${theme.fontWeight.regular};
`;
const sizeStyles = ({ theme, size = 'one' }) => {
    const sizeMap = {
        one: {
            marginBottom: theme.spacings.byte,
            paddingLeft: theme.spacings.kilo,
            marginLeft: theme.spacings.kilo,
            type: typography('one')(theme),
        },
        two: {
            marginBottom: theme.spacings.kilo,
            paddingLeft: theme.spacings.kilo,
            marginLeft: theme.spacings.bit,
            type: typography('two')(theme),
        },
    };
    const { marginBottom, paddingLeft, marginLeft, type } = sizeMap[size];
    return css `
    padding-left: ${paddingLeft};
    ${type};

    li {
      margin-bottom: ${marginBottom};
      margin-left: ${marginLeft};
      &:last-child {
        margin-bottom: 0;
      }
    }

    ul,
    ol {
      margin-bottom: ${marginBottom};
      margin-left: ${marginLeft};
      &:last-child {
        margin-bottom: 0;
      }
    }
  `;
};
const BaseList = styled('ul', {
    shouldForwardProp: (prop) => isPropValid(prop) && prop !== 'size',
})(baseStyles, sizeStyles);
/**
 * A list, which can be ordered or unordered.
 */
export const List = forwardRef((_a, ref) => {
    var { variant } = _a, props = __rest(_a, ["variant"]);
    return (_jsx(BaseList, Object.assign({ as: variant === 'ordered' ? 'ol' : 'ul' }, props, { ref: ref })));
});
List.displayName = 'List';
